/**
 * @file components/blocks/test-persona-return.block.jsx
 *
 * 🔙 THE WAY BACK FROM "TEST AS…".
 *
 * The switcher swaps the session outright: it writes the persona's access
 * token over the operator's and lands on `/`. From that moment the operator IS
 * that person — and the switcher itself is hidden for non-operators, correctly
 * and deliberately ("plain customers and worker-track users have no business
 * toggling identities").
 *
 * So there was no way back. Walked 2026-09-10: switched from super_admin into
 * a reviewer to claim a job, and nothing on any page the reviewer can reach
 * offered a return — only signing out cleared it, and the operator's own
 * session was already gone.
 *
 * The refusal notice made the gap visible in its own words: *"You hold it, but
 * you are viewing as another role. Switch back with 'Test as…' in the
 * header."* — advice that cannot be followed from the persona it is given in.
 *
 * This renders only when a return session is stashed, which only the switcher
 * does, which is dev-only. In every other case it is nothing.
 */

import { useEffect, useState } from 'react';
import { storage } from '../../utils/storage';
import { Button } from '../elements/Button.jsx';

export default function TestPersonaReturn() {
  const [stash, setStash] = useState(null);

  useEffect(() => {
    if (!import.meta.env?.DEV) return;
    try { setStash(storage.get(storage.KEYS.TEST_PERSONA_RETURN) || null); } catch { setStash(null); }
  }, []);

  if (!import.meta.env?.DEV || !stash?.accessToken) return null;

  const goBack = () => {
    try {
      // The stamp is dropped for the same reason the switch drops it: the
      // identity guard has to learn the new person on the next hydrate rather
      // than compare them against the one it remembers.
      storage.remove(storage.KEYS.USER_ID);
      storage.set(storage.KEYS.ACCESS_TOKEN, stash.accessToken);
      if (stash.refreshToken) storage.set(storage.KEYS.REFRESH_TOKEN, stash.refreshToken);
      storage.remove(storage.KEYS.TEST_PERSONA_RETURN);
    } catch { /* storage blocked; the assign below still clears the banner */ }
    // Root, not the current page: the operator's identity may not be able to
    // open whatever the persona was looking at — the same reason the switch
    // lands on `/`.
    window.location.assign('/');
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] flex items-center justify-center gap-3 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
      <span>
        Testing as another account.
        {stash.email ? <> You are signed in as <strong>{stash.email}</strong> underneath.</> : null}
      </span>
      <Button variant="outline" size="sm" icon={false} onClick={goBack} title="Return to my account">
        Switch Back
      </Button>
    </div>
  );
}
